import type { FeatureRecord, FeatureStatus } from '../generated/protocol'

// The timeline strip's arithmetic (ui.md 3.9): the rollback marker's wording, item status wording,
// and the gaps a drag drops into. The marker's position is core/recipe.ts markerPosition.

/** The status the item shows: the latest recomputed one, or pending before it (3.9). */
export function itemStatus(status: FeatureStatus | undefined): FeatureStatus['status'] | 'pending' {
  return status?.status ?? 'pending'
}

/** The hidden description of an item's status, or null for ok and pending. */
export function statusText(status: FeatureStatus | undefined, suppressed: boolean): string | null {
  const first = status?.messages[0]?.message
  switch (status?.status) {
    case 'error':
      return first === undefined ? 'Error' : `Error: ${first}`
    case 'warning':
      return first === undefined ? 'Warning' : `Warning: ${first}`
    case 'suppressed':
      return 'Suppressed'
    case 'rolled_back':
      return 'Rolled back'
    default:
      return suppressed ? 'Suppressed' : null
  }
}

/** A laid out item's horizontal extent, in client CSS pixels. */
export type Extent = { readonly left: number; readonly right: number }

/**
 * The x positions of the gaps 0 to N around items laid out left to right: halfway between
 * neighbors, and half the items' spacing outside the first and the last. Without items there is
 * one gap, at `start`.
 */
export function gapsBetween(items: readonly Extent[], start: number): readonly number[] {
  const [first, second] = items
  const last = items.at(-1)
  if (first === undefined || last === undefined) {
    return [start]
  }
  const spacing = second === undefined ? 0 : second.left - first.right
  const gaps = [first.left - spacing / 2]
  for (let i = 1; i < items.length; i++) {
    const before = items[i - 1]
    const after = items[i]
    if (before !== undefined && after !== undefined) {
      gaps.push((before.right + after.left) / 2)
    }
  }
  gaps.push(last.right + spacing / 2)
  return gaps
}

/** The gaps around the timeline items of a strip as laid out now. */
export function gapPositions(strip: HTMLElement): readonly number[] {
  const items = [...strip.querySelectorAll<HTMLElement>('[data-feature-id]')].map((item) =>
    item.getBoundingClientRect(),
  )
  return gapsBetween(items, strip.getBoundingClientRect().left)
}

/**
 * The timeline index a drop at gap `gap` (0 to N) moves the feature at index `from` to (3.9):
 * `gap` when it is at most `from`, else `gap - 1`, so the gaps on either side of the feature leave
 * it in place.
 */
export function dropIndex(from: number, gap: number): number {
  return gap <= from ? gap : gap - 1
}

/** The gap nearest to a client x position. */
export function nearestGap(gaps: readonly number[], x: number): number {
  let best = 0
  gaps.forEach((gap, index) => {
    if (Math.abs(gap - x) < Math.abs((gaps[best] ?? gap) - x)) {
      best = index
    }
  })
  return best
}

/** The marker's spoken position: "At the start", "After {label}" or "At the end" (3.9). */
export function markerText(
  records: readonly FeatureRecord[],
  position: number,
  labelOf: (id: string) => string,
): string {
  const record = records[position - 1]
  if (position >= records.length) {
    return 'At the end'
  }
  return record === undefined ? 'At the start' : `After ${labelOf(record.id)}`
}
